import { useEffect, useRef, useState } from "react";
import { useField } from "formik";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  ErrorMessage,
  Flex,
  ModalV2,
  SearchOption,
  toast,
  TypeaheadInputComponent,
} from "@jsq/ds/core";
import {
  FormCheckbox,
  FormRadioButton,
  FormRadioGroup,
  FormTextInput,
  useFormContext,
} from "@jsq/ds/forms";
import { globalEnv } from "~/global-env";
import {
  useEntityFromProspectLazyQuery,
  useGetComplianceCaseByProspectLazyQuery,
} from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { toDbId } from "~/utils/graphql-helpers";
import {
  type CaseInformationFormData,
  useCaseFormContext,
} from "../../CreateCaseNavigation/CaseFormContext";
import { extractIdFromPathSegment } from "../../utils";
import { EntityOption } from "../CaseInformationCard/types";
import { FormValues } from "../types";
import ArenaTypeahead from "./ArenaTypeahead";
import EntityTypeahead from "./EntityTypeahead";
import { cardStyle } from "./styles_vanilla.css";

type Props = {
  setProspectId: (arg: number | null) => void;
};

const CaseInformationCard = ({ setProspectId }: Props) => {
  const history = useHistory();
  const { values, errors, setFieldValue } = useFormContext<FormValues>();
  const caseFormContext = useCaseFormContext();
  const arenaTypeaheadInputRef = useRef<TypeaheadInputComponent>(null);
  const entityTypeaheadInputRef = useRef<TypeaheadInputComponent>(null);
  const valuesRef = useRef<Partial<CaseInformationFormData>>({});
  const [existingCaseId, setExistingCaseId] = useState<number | null>(null);

  /**
   * These are used to avoid showing errors before the user has touched the respective field
   */
  const [_c, clientMeta] = useField<FormValues>("client");
  const [_e, entityMeta] = useField<FormValues>("entity");

  const [investmentEntityFromProspect] = useEntityFromProspectLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [getComplianceCaseByProspect] = useGetComplianceCaseByProspectLazyQuery({
    fetchPolicy: "no-cache",
  });

  /**
   * @returns Either default placeholder empty text or the name of the selected client (arena)
   *          once a prospect id or subscription url has been entered
   */
  const getClientValue = () => {
    return values.client ? values.client.name : "";
  };
  /**
   * @returns Either default placeholder empty text or the name of the selected entity
   *          once a prospect id or subscription url has been entered
   */
  const getEntityValue = () => {
    return values.entity ? values.entity.name : "";
  };

  /**
   * When the user selects a client (arena) we need to store off the id and name.
   * @param selectedOption Contains id & label (name) to store off.
   */
  const onSelectClient = (selectedOption: SearchOption) => {
    const clientObject = { id: selectedOption.id, name: selectedOption.label };
    void setFieldValue("client", clientObject);
    void setFieldValue("entity", null);
    void setFieldValue("programName", undefined);
    entityTypeaheadInputRef.current?.clearInputValue();
  };

  /**
   * When the user selects an entity (institution) we need to store off the id and name.
   * @param selectedOption Contains id & label (name) to store off.
   */
  const onSelectEntity = (selectedOption: EntityOption) => {
    const entityObject = { id: selectedOption.id, name: selectedOption.label };
    void setFieldValue("programName", selectedOption.program);
    void setFieldValue("entity", entityObject);
  };

  const onUpdate = async () => {
    const prospectId = extractIdFromPathSegment(values.prospectIdOrSubscriptionUrl, "prospect");

    // Cannot continue if the prospect ID was not extracted
    if (!prospectId) {
      toast.error("Prospect ID could not be deciphered");
      return;
    }

    const results = await investmentEntityFromProspect({
      variables: { prospectId: prospectId.toString(), includeDeleted: false },
    });

    const entity = results?.data?.investmentEntityFromProspect;
    const arena = entity?.arena;
    const programName = entity?.complianceSettings?.complianceProgram;

    if (results.error || !entity || !arena) {
      toast.error(
        `Failed to get client and entity information using prospect id ${prospectId}`,
      );
      return;
    }

    let arenaDbId: number;
    try {
      arenaDbId = toDbId(arena.id);
    } catch {
      toast.error(`Failed to resolve arena ID for prospect ${prospectId}`);
      return;
    }

    const { data, error: caseQueryError } = await getComplianceCaseByProspect({
      variables: {
        arenaId: arenaDbId,
        prospectId: Number(prospectId),
      },
    });

    if (caseQueryError) {
      const isUnauthorized = caseQueryError.graphQLErrors?.some((e) =>
        e.message?.includes("not authorized for one or more workspaces"),
      );
      toast.error(
        isUnauthorized
          ? "You are not authorized to access this workspace"
          : `Failed to check for existing compliance cases for prospect ${prospectId}`,
      );
      return;
    }

    const existingCaseId = data?.complianceCaseByProspectId?.id;
    if (existingCaseId) {
      setExistingCaseId(existingCaseId);
      return;
    }

    // Clear the input fields
    arenaTypeaheadInputRef.current?.clearInputValue();
    entityTypeaheadInputRef.current?.clearInputValue();

    setProspectId(prospectId);

    const arenaObject = { id: arena.id, name: arena.name };
    const entityObject = { id: entity.id, name: entity.name };
    void setFieldValue("client", arenaObject);
    void setFieldValue("entity", entityObject);
    void setFieldValue("programName", programName);
  };

  // Keep ref updated with latest values
  useEffect(() => {
    valuesRef.current = {
      hasSubscriptionUrl: values.hasSubscriptionUrl,
      prospectIdOrSubscriptionUrl: values.prospectIdOrSubscriptionUrl,
      client: values.client,
      entity: values.entity,
      assignToMyself: values.assignToMyself,
      accountType: values.accountType,
      entityType: values.entityType,
      programName: values.programName,
    };
  }, [values]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (caseFormContext?.saveCaseInformationData) {
        caseFormContext.saveCaseInformationData(valuesRef.current);
      }
    };
  }, [caseFormContext]);

  return (
    <>
      <Card className={cardStyle} title="Case information">
        <FormRadioGroup
          style={{ marginBottom: SPACING.MEDIUM }}
          name="hasSubscriptionUrl"
          label="Do you have a prospect ID or a subscription URL?"
        >
          <FormRadioButton value="yes" data-testid="has-subscription-url">
            Yes
          </FormRadioButton>
          <FormRadioButton value="no" data-testid="does-not-have-subscription-url">
            No
          </FormRadioButton>
        </FormRadioGroup>
        {values.hasSubscriptionUrl === "yes" && (
          <Flex justifyContent="space-between" gap={8} alignItems="center">
            <FormTextInput
              label="Prospect ID or Subscription URL"
              name="prospectIdOrSubscriptionUrl"
              data-testid="prospect-id-or-subscription-url"
              isFullWidth
              css={{ marginBottom: SPACING.MEDIUM }}
              className="stacked-md"
            />
            <Button
              variant="neutral"
              onClick={() => void onUpdate()}
              css={{ marginTop: SPACING.SMALL }}
            >
              Update
            </Button>
          </Flex>
        )}

        <Flex style={{ marginBottom: SPACING.MEDIUM }} gap={8} flexDirection="row">
          <Flex
            flexDirection="column"
            style={{ marginRight: SPACING.SMALL, maxWidth: "250px" }}
          >
            <ArenaTypeahead
              readOnly={values.hasSubscriptionUrl === "yes"}
              label="Client"
              name="client"
              placeholder={values.client ? getClientValue() : ""}
              initialSearchValue={values.client ? getClientValue() : undefined}
              onSelectOption={onSelectClient}
              onClearValue={() => {
                void setFieldValue("client", null);
              }}
              arenaTypeaheadInputRef={arenaTypeaheadInputRef}
            ></ArenaTypeahead>
            {clientMeta.touched && errors?.client && (
              <ErrorMessage>{errors?.client.toString()}</ErrorMessage>
            )}
          </Flex>
          <Flex flexDirection="column" css={{ maxWidth: "250px" }}>
            <EntityTypeahead
              readOnly={values.hasSubscriptionUrl === "yes"}
              label="Entity"
              name="entity"
              arenaId={values.client?.id}
              placeholder={values.entity ? getEntityValue() : ""}
              initialSearchValue={values.entity ? getEntityValue() : undefined}
              onSelectOption={onSelectEntity}
              onClearValue={() => {
                void setFieldValue("entity", null);
              }}
              entityTypeaheadInputRef={entityTypeaheadInputRef}
            ></EntityTypeahead>
            {entityMeta.touched && errors?.entity && (
              <ErrorMessage>{errors?.entity.toString()}</ErrorMessage>
            )}
          </Flex>
        </Flex>
        <FormCheckbox name="assignToMyself" label="Assign case to myself"></FormCheckbox>
      </Card>
      <ModalV2
        show={!!existingCaseId}
        closeButton={true}
        closeOnClickOutside={false}
        escapeToClose={false}
        onHide={() => setExistingCaseId(null)}
        title="Case Already Exists"
        size="small"
        data-testid="case-exists-modal"
      >
        <ModalV2.Body>A compliance case already exists for this prospect.</ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap="md">
            <Button
              variant="primary"
              onClick={() => {
                setExistingCaseId(null);
                history.push(`${globalEnv.complianceRoute}/case/${existingCaseId}`);
              }}
            >
              View Case
            </Button>
          </Flex>
        </ModalV2.Footer>
      </ModalV2>
    </>
  );
};

export default CaseInformationCard;
